import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'gatsby';
import { styled } from '@mui/material/styles';
import {
  Button, Grid, Menu, MenuItem, Typography, Box,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Section from '../Section';
import SectionWaveEffect from '../SectionWaveEffect';
import pop from '../../themes/ThemeProviders/colors/pop';

const PublisherImg = styled('img')(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  display: 'block',
}));

const section = (theme) => ({
  [theme.breakpoints.down('md')]: {
    paddingTop: theme.spacing(8),
  },
  paddingTop: theme.spacing(8),
  paddingBottom: theme.spacing(16),
});

const ctaButton = () => ({
  minWidth: 250,
  marginBottom: '1rem',
  marginRight: '0.5rem',
});

const CTAPrimaryButton = styled(Button)(({ theme }) => ({
  color: theme.palette.getContrastText(pop[400]),
  backgroundColor: pop[400],
  '&:hover': {
    backgroundColor: pop[600],
  },
}));

const CTASecondaryButton = styled(Button)(({ theme }) => ({
  color: pop[400],
  border: `1px solid ${pop[400]}`,
  backgroundColor: theme.palette.common.white,
  '&:hover': {
    border: `1px solid ${pop[400]}`,
    backgroundColor: theme.palette.grey[50],
  },
}));

function NualangForPublishers({
  title, subtitle1, subtitle2, image, demoButtonText, brochureButtonText, versions,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onDownload = (version) => {
    const link = document.createElement('a');
    link.download = version.download;
    link.href = version.href;
    link.click();
  };

  return (
    <React.Fragment>
      <Box
        sx={() => ({
          backgroundColor: '#FCDDEC',
          position: 'relative',
        })}
      >
        <Section sx={section}>
          <Grid
            container
            spacing={2}
          >
            <Grid item xs={6} sm={6}>
              <PublisherImg
                src={image}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                sx={(theme) => ({
                  [theme.breakpoints.up('md')]: {
                    lineHeight: 1.75,
                    fontSize: '25px',
                    marginTop: '1.5rem',
                    marginBottom: '1.5rem',
                  },
                  lineHeight: 1.75,
                  color: theme.palette.primary.main, // '#AD3DAA',
                  fontSize: '15px',
                  marginBottom: '1rem',
                })}
                variant="h6"
                component="h6"
                textAlign={'start'}
                fontWeight={'bold'}
              >
                {subtitle1}
              </Typography>
              <Typography
                sx={(theme) => ({
                  [theme.breakpoints.up('md')]: {
                    fontSize: '48px',
                    lineHeight: '65px',
                    marginBottom: '1.5rem',
                  },
                  fontWeight: 'bold',
                  fontSize: '30px',
                  lineHeight: '40px',
                  marginBottom: '1rem',
                })}
                variant="h1"
                component="h1"
              >
                {title}
              </Typography>
              <Typography
                sx={(theme) => ({
                  [theme.breakpoints.up('md')]: {
                    fontSize: '24px',
                    lineHeight: '32px',
                    marginBottom: '1.5rem',
                  },
                  fontWeight: 'normal',
                  fontSize: '16px',
                  lineHeight: '24px',
                  marginBottom: '1rem',
                })}
                variant="h4"
                component="h4"
              >
                {subtitle2}
              </Typography>
              <CTAPrimaryButton
                variant="contained"
                component={Link}
                to="#publisher-contact-form"
                sx={ctaButton}
              >
                {demoButtonText}
              </CTAPrimaryButton>
              <CTASecondaryButton
                variant="outlined"
                color="primary"
                sx={ctaButton}
                onClick={handleClick}
                startIcon={<PictureAsPdfIcon />}
                endIcon={open
                  ? <ExpandLessIcon />
                  : <ExpandMoreIcon />
                }
                id="brochure-button"
              >
                {brochureButtonText}
              </CTASecondaryButton>
              <Menu
                id="brochure-menu"
                aria-labelledby="brochure-button"
                fullWidth
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
                sx={{ alignItems: 'center' }}
              >
                {
                  versions?.map((brochureVersion) => (
                    <MenuItem
                      key={brochureVersion.href}
                      onClick={() => onDownload(brochureVersion)}
                      sx={{
                        marginRight: 4,
                        marginLeft: 4,
                        justifyContent: 'center',
                      }}
                    >
                      {brochureVersion.label}
                    </MenuItem>
                  ))
                }
              </Menu>
            </Grid>
          </Grid>
        </Section>
        <SectionWaveEffect
          pathClassName="shape-fill-white"
        />
      </Box>
    </React.Fragment>
  );
}

NualangForPublishers.propTypes = {
  title: PropTypes.string.isRequired,
  version: PropTypes.PropTypes.array.isRequired,
  subtitle1: PropTypes.string.isRequired,
  subtitle2: PropTypes.string.isRequired,
  image: PropTypes.shape({ type: PropTypes.oneOf(['img', 'svg']) }),
  sections: PropTypes.array.isRequired,
  demoButtonText: PropTypes.string.isRequired,
  brochureButtonText: PropTypes.string.isRequired,
  versions: PropTypes.array.isRequired,
};

export default NualangForPublishers;
